import { h, render, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { IntlProvider, Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';
import Markdown from 'preact-markdown';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import PubSub, { topics } from '../../lib/pubsub';
import SyncBanner from '../../components/gui/syncBanner';
import { canReopenJournal, isValidReopenReason, canDeleteJournal, canUndeleteJournal, isValidDeleteReason } from '../../lib/journalAudit';

import ViewFields from '../../components/form/viewFields';
import Input from '../../components/form/input';
import DyrejournalSaleSearch from '../../components/dyrejournal/search';
import DyrejournalReportPrint from '../../components/dyrejournal/reportPrint';
import Pagination from '../../components/gui/pagination';
import DyrejournalSalePrescription from '../../components/dyrejournal/salePrescription';
import { appAlert, appConfirm } from '../../lib/dialogs';
import { beginDialogAction } from '../../lib/dialogAction';

const fieldSorter = (fields) => (a, b) => fields.map(o => {
    let dir = 1;
    if (o[0] === '-') {
        dir = -1;
        o = o.substring(1);
    }
    return a[o] > b[o] ? dir : a[o] < b[o] ? -(dir) : 0;
}).reduce((p, n) => p ? p : n, 0);

function getInitials(name) {
    if (!name) return '';
    return name
        .split(' ')  // Split the name into an array of words
        .filter(word => word.length > 0)  // Filter out any empty strings
        .map(word => word[0].toUpperCase())  // Map each word to its first character and convert to uppercase
        .join('');  // Join the initials into a single string
}

@withText(props => ({
    productTitle: <Text id='dyrejournal.product.search-title'>Search for product</Text>,
    searchTitle: <Text id='dyrejournal.journal-search-title'>Search for journals</Text>,
    switchClinicConfirm: <Text id='dyrejournal.journal-switch-clinic-confirm'>This sale is registered on another clinic. Switch to that clinic to open the sale?</Text>,
    unlockAreYouSure: <Text id='dyrejournal.journal-unlock-areyousure'>Are you sure you want to unlock journal?</Text>,
    reopenAreYouSure: <Text id='dyrejournal.journal-reopen-areyousure'>You are about to reopen this journal. The action is logged in the journal history.</Text>,
    reopenReasonPlaceholder: <Text id='journal.reopenReason-placeholder'>Why is this journal being reopened?</Text>,
    deleteAreYouSure: <Text id='dyrejournal.journal-delete-areyousure'>You are about to delete this journal. The action is logged in the journal history.</Text>,
    deleteReasonPlaceholder: <Text id='journal.deleteReason-placeholder'>Why is this journal being deleted?</Text>,
    animaliaSearchTitle: <Text id='dyrejournal.journal-animalia-search-title'>Search for individuals in DHP</Text>,
    animalTitle: <Text id='animal.title'>Animal</Text>,
    journalTitle: <Text id='dyrejournal.journal-title'>Journal</Text>,
    prescriptionTitle: <Text id='dyrejournal.prescription-title'>Prescription</Text>,
    medicinesTitle: <Text id='dyrejournal.medicines-title'>Medicines</Text>,
    diagnosesTitle: <Text id='dyrejournal.diagnoses-title'>Diagnoses</Text>,
}))
@observer
class AllJournals extends Component {
	constructor(props) {
        super(props);
        this.state = {
            selected: new Set(),   // holds line ids
            sections: fields.get('journal'),
            reportDate: util.isoDate(new Date(), false, false, true),
            closeBeforeCutoffDate: '',
            closeByDateCount: null,
            closeByDateLoading: false,
            syncJournalNotification: null,
            showUnlockConfirm: false,
            unlockJournalId: null,
            unlockReason: '',
            showDeleteConfirm: false,
            deleteJournalId: null,
            deleteReason: '',
        };
        this.searchTimer = null;
        this.selectAllRef = null;
        this.syncJournalSubscription = null;
    }

    loadAll = async (props = this.props, emptyFirst = true) => {
        const { showDeleted } = this.state;

        const { journalStore } = this.props.stores;
        const { cutoffDate, cutoffDateEnd, activeStatus, veterinary, animalSpeciesId, searchText, currentPage, limit, templateFilter } = journalStore;
        const response = await journalStore.load({
            query: {
                search: searchText,
                showDeleted,
                cutoffDate,
                cutoffDateEnd,
                veterinary,
                animalSpeciesId,
                templateFilter: templateFilter || null,
                closed: activeStatus === 'closed' ? 'true' : (activeStatus === 'open' ? 'false' : null),
            },
            skipUpdate: true,
            offset: (currentPage - 1) * limit,
            limit,
            addData: ['animals', 'visitors', 'veterinaries', 'totalJournals', 'products'],
        });
        journalStore.updateKeyValue('allJournals', Array.isArray(response) ? response : []);
        await journalStore.loadVeterinaries();
        if (!journalStore.animalSpeciesList || journalStore.animalSpeciesList.length === 0) {
            await journalStore.loadAnimalSpeciesList();
        }
    }

    saveSale = async (props = {}) => {
        const { saleStore } = this.props.stores;
        // Never let journal-triggered sale saves touch payment/status fields.
        // The local newSale state can be stale (e.g. loaded before a concurrent
        // payment was registered), and flushing it wholesale would silently
        // revert paid sales back to status=0 / paymentMethod="". See patch-sale
        // allowedFields — these fields pass through even on locked sales.
        await saleStore.saveSale({
            ...props,
            fieldsToSkip: ['paymentMethod', 'paymentAmount', 'status', 'paidDate'],
        });
    }

    loadJournals = async animalIds => {
        const { journalStore, saleStore } = this.props.stores;
        const response = await journalStore.load({
            query: {
                animal: animalIds,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('animalJournals', response);
    }

    onClickEditJournal = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { id, sale } = e.target.closest('.journalLine').dataset;
        const {
            drawerLevel = 1,
            saleIsLocked = false,
        } = this.props;
		const { appState, saleStore, journalStore } = this.props.stores;

        const journalId = parseInt(id, 10);
        const journalSaleId = parseInt(sale, 10);

        // Do NOT load this journal's sale into saleStore.newSale. The editor loads its
        // own animal/owner from the journal itself, and saleStore.load() is scoped to
        // the ACTIVE clinic — for a journal owned by another clinic the load returns
        // nothing and would leave newSale pointing at a stale/wrong sale (this is the
        // "wrong owner when going back to register payment" bug, and it can even save
        // over the wrong sale). get-sale already refreshes embedded sale.journals on
        // every read (populateSaleListWithJournals), so the only time we need to keep
        // newSale in sync from here is when it already holds THIS exact sale.
        const syncSaleIfCurrent = async (saveProps = {}) => {
            const { newSale } = saleStore;
            if (!newSale?.id || newSale.id !== journalSaleId) {
                return;
            }
            await this.saveSale(saveProps);
        };

		appState.openDrawerRight('editJournal', {
            isNew: false,
            hideAdmin: true,
            saveSale: syncSaleIfCurrent,
            saleIsLocked,
            id: journalId,
            callback: async () => {
                const { newJournal } = journalStore;
                const { newSale } = saleStore;
                // Only touch the sale when the sale page already holds THIS journal's
                // sale; otherwise newSale belongs to a different (active-clinic) sale
                // and splicing into it would corrupt the wrong sale.
                if (newSale?.id && newSale.id === journalSaleId) {
                    const { journals = [] } = newSale;
                    const idx = journals.findIndex(a => a.id === newJournal.id);
                    if (idx > -1) {
                        saleStore.updateObjectKeyArrValue('newSale', 'journals', idx, newJournal);
                    } else {
                        saleStore.updateObjectKeyValue('newSale', 'journals', [...journals, newJournal]);
                    }
                    // Re-sync only the journals array — never flush stale status/paymentMethod.
                    await this.saveSale({
                        calledFrom: 'journals.onClickEditJournal.callback',
                        fieldsToSave: ['journals'],
                    });
                }
                this.loadAll();
            },
		}, drawerLevel + 1);
    }

    onClickGotoSale = async e => {
        e.stopPropagation();
        e.preventDefault();
        const dialogAction = beginDialogAction(this, 'onClickGotoSale');
        if (!dialogAction) return false;
        try {
            // Open the sale linked to this journal on the sales page, ready for payment.
            // Stops propagation so the row's edit-journal handler does not also fire.


            const el = e.target.closest('.gotoSaleLink');
            if (!el) {
                return;
            }
            const saleId = parseInt(el.dataset.sale, 10);
            const journalCustomerId = parseInt(el.dataset.customer, 10);
            if (!saleId) {
                return;
            }
            const { drawerLevel = 1, switchClinicConfirm } = this.props;
            const { appState, userStore, saleStore, journalStore, visitorStore, animalStore, calendarStore } = this.props.stores;
            const activeCustomerId = userStore.customer?.id;

            // Sales are scoped to a single clinic, but the journal list spans every clinic
            // the user has access to. If this sale belongs to another clinic than the
            // active one, confirm and switch clinic first so the sale — and any payment
            // registered on it — lands on the correct clinic.
            if (journalCustomerId && activeCustomerId && journalCustomerId !== activeCustomerId) {
                const clinicName = (userStore.currentCustomers || []).find(c => c.id === journalCustomerId)?.name || '';
                const message = clinicName ? `${switchClinicConfirm}\n\n${clinicName}` : `${switchClinicConfirm}`;
                if (!((await appConfirm(message)) && dialogAction.isCurrent())) {
                    return;
                }
                // Clear cached data, then switch the active clinic (same flow as the clinic
                // switcher in the menu) before navigating to the sale.
                saleStore?.clearAll();
                journalStore?.clearAll();
                visitorStore?.clearAll();
                animalStore?.clearAll();
                calendarStore?.clearAll();
                await userStore.save({ customer: journalCustomerId });
                await userStore.getInfo({ forceLoad: true });
            }

            appState.toggleDrawerRight(false, drawerLevel);
            route(`/dyrejournal-sale/${saleId}`);
        } finally {
            dialogAction.finish();
        }
    }

    onClickViewJournal = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.journalLine').dataset;
        const { drawerLevel = 1 } = this.props;
        const { appState } = this.props.stores;
        const { drawerHeightLarge } = appState;

        appState.openDrawer('viewJournal', {
            hideAdmin: true,
            height: drawerHeightLarge,
            id: parseInt(id, 10),
        }, drawerLevel + 2);
    }

    onClickUnlock = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.journalLine').dataset;
        // Open a prominent, centered in-app confirmation modal instead of the native
        // browser confirm() that pops up as an easy-to-miss little bar at the top of the
        // page (TICKET-01156). The real unlock logic runs in confirmUnlockJournal().
        this.setState({
            showUnlockConfirm: true,
            unlockJournalId: parseInt(id, 10),
            unlockReason: '',
        });
    }

    cancelUnlockJournal = () => {
        this.setState({ showUnlockConfirm: false });
    }

    confirmUnlockJournal = async () => {
        const { unlockJournalId, unlockReason } = this.state;
        // TICKET-01342: reason is mandatory — the confirm button is disabled
        // until valid, this is just a belt-and-braces guard.
        if (!isValidReopenReason(unlockReason)) {
            return;
        }
        this.setState({ showUnlockConfirm: false });
        const { journalStore } = this.props.stores;

        const response = await journalStore.reopen(unlockJournalId, unlockReason.trim());
        if (response?.status === 202) {
            this.loadAll();
        }
    }

    onClickDeleteJournal = e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.journalLine').dataset;
        // Same prominent centered modal as reopen (TICKET-01156) — the reason
        // is mandatory and stored in the journal's audit trail.
        this.setState({
            showDeleteConfirm: true,
            deleteJournalId: parseInt(id, 10),
            deleteReason: '',
        });
    }

    cancelDeleteJournal = () => {
        this.setState({ showDeleteConfirm: false });
    }

    confirmDeleteJournal = async () => {
        const { deleteJournalId, deleteReason } = this.state;
        if (!isValidDeleteReason(deleteReason)) {
            return;
        }
        this.setState({ showDeleteConfirm: false });
        const { journalStore } = this.props.stores;

        const response = await journalStore.deleteJournal(deleteJournalId, deleteReason.trim());
        if (response?.status === 202) {
            this.loadAll();
        }
    }

    onClickUndeleteJournal = async e => {
        e.stopPropagation();
        e.preventDefault();
        const { id } = e.target.closest('.journalLine').dataset;
        const { journalStore } = this.props.stores;
        const response = await journalStore.undeleteJournal(parseInt(id, 10));
        if (response?.status === 202) {
            this.loadAll();
        }
    }

    searchInput = (e) => {
        const searchText = e.target.value;
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('searchText', searchText);

        clearTimeout(this.searchTimer);
        if (searchText.length >= 3) {
            this.searchTimer = setTimeout(async () => {
                this.doSearch(searchText);
            }, 1000);
        }
    }

    async doSearch() {
        this.setState({ searching: true });
        const { showDeleted } = this.state;
        const { journalStore } = this.props.stores;
        const { cutoffDate, cutoffDateEnd, activeStatus, veterinary, animalSpeciesId, searchText, currentPage, limit, templateFilter } = journalStore;

        if (searchText) {
            const response = await journalStore.load({
                query: {
                    search: searchText,
                    showDeleted,
                    cutoffDate,
                    cutoffDateEnd,
                    veterinary,
                    animalSpeciesId,
                    templateFilter: templateFilter || null,
                    closed: activeStatus === 'closed' ? 'true' : (activeStatus === 'open' ? 'false' : null),
                }, limit, skipUpdate: true, addData: ['animals', 'visitors', 'veterinaries', 'totalJournals', 'products'] });
            journalStore.updateKeyValue('allJournals', Array.isArray(response) ? response : []);
        } else {
            const response = await journalStore.load({
                query: {
                    showDeleted,
                    cutoffDate,
                    cutoffDateEnd,
                    veterinary,
                    animalSpeciesId,
                    templateFilter: templateFilter || null,
                    closed: activeStatus === 'closed' ? 'true' : (activeStatus === 'open' ? 'false' : null),
                }, limit, skipUpdate: true, addData: ['animals', 'visitors', 'veterinaries', 'totalJournals', 'products'] });
            journalStore.updateKeyValue('allJournals', Array.isArray(response) ? response : []);
        }
        this.setState({ searching: false });
    }

    clearSearch = () => {
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('searchText', '');
        this.doSearch('');
    }

    handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            this.doSearch();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            this.clearSearch();
        }
    }

    setPage = (page) => {
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('currentPage', page);
        this.loadAll();
    }

    toggleShowDeleted = async e => {
        const { checked } = e.target;
        this.setState({
            showDeleted: checked,
        }, () => {
            this.loadAll();
        });
    }

    changeCutoffDate = async e => {
        const { value: cutoffDate } = e.target;
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('cutoffDate', cutoffDate);
        this.loadAll();
    }

    changeCutoffDateEnd = async e => {
        const { value: cutoffDateEnd } = e.target;
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('cutoffDateEnd', cutoffDateEnd);
        this.loadAll();
    }

    changeVeterinary = async e => {
        const { value: veterinary } = e.target;
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('veterinary', veterinary);
        this.loadAll();
    }

    changeAnimalSpeciesId = async e => {
        const { value } = e.target;
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('animalSpeciesId', value || null);
        journalStore.updateKeyValue('currentPage', 1);
        this.loadAll();
    }

    onClickGoToToday = e => {
        e.stopPropagation();
        e.preventDefault();
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('cutoffDate', util.isoDate(new Date(), false, false, true));
        journalStore.updateKeyValue('cutoffDateEnd', util.isoDate(new Date(), false, false, true));
        this.loadAll();
    }

    clearCutoffDate = e => {
        e.stopPropagation();
        e.preventDefault();
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('cutoffDate', null);
        this.loadAll();
    }

    clearCutoffDateEnd = e => {
        e.stopPropagation();
        e.preventDefault();
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('cutoffDateEnd', null);
        this.loadAll();
    }

    onChooseLimit = async e => {
        const { value } = e.target;
        const limit = parseInt(value, 10);
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('limit', limit);
        this.loadAll();
    }

    onChooseStatus = async e => {
        const { value } = e.target;
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('activeStatus', value);
        this.loadAll();
    }

    onChooseTemplateFilter = async e => {
        const { value } = e.target;
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('templateFilter', value || null);
        journalStore.updateKeyValue('currentPage', 1);
        this.loadAll();
    }

    onChooseMode = async e => {
        const { value } = e.target;
        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('activeMode', value);
        this.loadAll();
    }

    onClickToggleFormatting = e => {
        const { formatting } = this.state;
        this.setState({ formatting: !formatting });
    }

    copyNumber = (e, number) => {
        e.stopPropagation();
        e.preventDefault();
        navigator.clipboard.writeText(number);
        const { numberCopied = {} } = this.props;
        numberCopied[number] = true;
        this.setState({ numberCopied });
        setTimeout(() => {
            numberCopied[number] = false;
            this.setState({ numberCopied });
        }, 2000);
    }

    onClickShowPrescriptions = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('small').dataset;
        const { drawerLevel = 2, prescriptionTitle } = this.props;
        const { appState, journalStore } = this.props.stores;
        const { drawerHeightSmall } = appState;

        const { allJournals } = journalStore;
        const currentJournal = allJournals.find(journal => journal.id === parseInt(id, 10));
        if (!currentJournal?.id) {
            console.warn('No journal found with id', id);
            return;
        }

        const currentJournalPrescriptions = currentJournal.prescriptions || [];

        appState.openDrawer('miniPrescription', {
            height: drawerHeightSmall,
            title: prescriptionTitle,
            prescriptions: currentJournalPrescriptions,
            journal: currentJournal,
        }, drawerLevel + 1);
    }

    onClickShowDiagnoses = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('small').dataset;
        const { drawerLevel = 2, diagnosesTitle } = this.props;
        const { appState, journalStore } = this.props.stores;
        const { drawerHeightSmall } = appState;

        const { allJournals } = journalStore;
        const currentJournal = allJournals.find(journal => journal.id === parseInt(id, 10));
        if (!currentJournal?.id) {
            console.warn('No journal found with id', id);
            return;
        }
        appState.openDrawer('miniDiagnoses', {
            height: drawerHeightSmall,
            title: diagnosesTitle,
            diagnoses: currentJournal.diagnoses,
        }, drawerLevel + 1);
    }

    onClickShowMedicines = e => {
        e.preventDefault();
        e.stopPropagation();
        const { id } = e.target.closest('small').dataset;
        const { drawerLevel = 2, medicinesTitle } = this.props;
        const { appState, journalStore } = this.props.stores;
        const { drawerHeightSmall } = appState;

        const { allJournals } = journalStore;
        const currentJournal = allJournals.find(journal => journal.id === parseInt(id, 10));
        if (!currentJournal?.id) {
            console.warn('No journal found with id', id);
            return;
        }

        appState.openDrawer('miniMedicine', {
            height: drawerHeightSmall,
            title: medicinesTitle,
            medicines: currentJournal.medicine,
        }, drawerLevel + 1);
    }

    visibleIds(rows) {
        // if you paginate/filter, pass the currently rendered rows via props
        return rows.filter(r => !r.closed).map(r => r.id);
    }

    isAllVisibleSelected(rows) {
        const ids = this.visibleIds(rows);
        return ids.length > 0 && ids.every(id => this.state.selected.has(id));
    }

    isSomeVisibleSelected(rows) {
        const ids = this.visibleIds(rows);
        const count = ids.filter(id => this.state.selected.has(id)).length;
        return count > 0 && count < ids.length;
    }

    setSelectAllIndeterminate(rows) {
        if (this.selectAllRef) {
            this.selectAllRef.indeterminate = this.isSomeVisibleSelected(rows) && !this.isAllVisibleSelected(rows);
        }
    }

    onToggleOne = (id, nextChecked, e) => {
        if (e) e.stopPropagation(); // keep row click from firing
        this.setState(prev => {
            const next = new Set(prev.selected); // copy, don't mutate
            if (nextChecked) next.add(id);
            else next.delete(id);
            return { selected: next };
        });
    };

    onToggleAllVisible = (rows, checked) => {
        const ids = this.visibleIds(rows);
        this.setState(s => {
            const next = new Set(s.selected);
            if (checked) {
                ids.forEach(id => next.add(id));
            } else {
                ids.forEach(id => next.delete(id));
            }
            return { selected: next };
        });
    }

    bulkCloseJournals = async () => {
        const dialogAction = beginDialogAction(this, 'bulkCloseJournals');
        if (!dialogAction) return false;
        try {
            const { selected } = this.state;
            const { journalStore } = this.props.stores;

            const ids = Array.from(selected.keys());

            // Ask user to be sure
            if (!((await appConfirm(`Are you sure you want to close these journals? (${ids.length})`)) && dialogAction.isCurrent())) {
                return;
            }

            if (ids.length === 0) return;

            await journalStore.bulkClose(ids);
            this.loadAll();
            this.setState({ selected: new Set() }); // clear selection after bulk close
        } finally {
            dialogAction.finish();
        }
    }

    onChangeCloseBeforeCutoffDate = e => {
        const closeBeforeCutoffDate = e.target.value;
        this.setState({ closeBeforeCutoffDate, closeByDateCount: null });
    }

    onClickCheckCloseByDate = async () => {
        const { closeBeforeCutoffDate } = this.state;
        const { journalStore } = this.props.stores;

        if (!closeBeforeCutoffDate) {
            (await appAlert('Please select a cutoff date first.'));
            return;
        }

        this.setState({ closeByDateLoading: true });
        const response = await journalStore.bulkCloseByDate(closeBeforeCutoffDate, true); // dryRun = true
        this.setState({
            closeByDateLoading: false,
            closeByDateCount: response?.data?.count ?? 0,
        });
    }

    onClickCloseByDate = async () => {
        const dialogAction = beginDialogAction(this, 'onClickCloseByDate');
        if (!dialogAction) return false;
        try {
            const { closeBeforeCutoffDate, closeByDateCount } = this.state;
            const { journalStore } = this.props.stores;

            if (!closeBeforeCutoffDate) {
                (await appAlert('Please select a cutoff date first.'));
                return;
            }

            if (closeByDateCount === null) {
                (await appAlert('Please check the number of journals first.'));
                return;
            }

            if (closeByDateCount === 0) {
                (await appAlert('No open journals found before this date.'));
                return;
            }

            // Confirm with the user
            const confirmed = ((await appConfirm(
                `Are you sure you want to close ${closeByDateCount} journals before ${closeBeforeCutoffDate}?\n\nThis action cannot be undone.`
            )) && dialogAction.isCurrent());

            if (!confirmed) {
                return;
            }

            this.setState({ closeByDateLoading: true });
            const response = await journalStore.bulkCloseByDate(closeBeforeCutoffDate, false); // dryRun = false

            if (response?.status === 202) {
                (await appAlert(`Successfully closed ${response.data.count} journals.`));
                this.setState({
                    closeByDateLoading: false,
                    closeByDateCount: null,
                    closeBeforeCutoffDate: '',
                });
                this.loadAll();
            } else {
                (await appAlert(`Error: ${response?.message || 'Failed to close journals.'}`));
                this.setState({ closeByDateLoading: false });
            }
        } finally {
            dialogAction.finish();
        }
    }

    changeYear = e => {
        // set fromDate and toDate to the selected year and month
        const currentYear = parseInt(e.target.value, 10);
        let { currentMonth } = this.state;
        if (!currentMonth) {
            currentMonth = 1;
        }
        const fromDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
        const toDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(new Date(currentYear, currentMonth, 0).getDate()).padStart(2, '0')}`;

        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('cutoffDate', fromDate);
        journalStore.updateKeyValue('cutoffDateEnd', toDate);

        this.setState({ currentYear }, () => {
            this.loadTimer = setTimeout(() => {
                this.loadAll();
            }, 700);
        });
    }

    changeMonth = e => {
        // set fromDate and toDate to the selected year and month
        const currentMonth = parseInt(e.target.value, 10);
        let { currentYear } = this.state;
        if (!currentYear) {
            currentYear = (new Date()).getFullYear();
        }
        const fromDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-01`;
        const toDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(new Date(currentYear, currentMonth, 0).getDate()).padStart(2, '0')}`;

        const { journalStore } = this.props.stores;
        journalStore.updateKeyValue('cutoffDate', fromDate);
        journalStore.updateKeyValue('cutoffDateEnd', toDate);

        this.setState({ currentMonth }, () => {
            this.loadTimer = setTimeout(() => {
                this.loadAll();
            }, 700);
        });
    }

    setDropdownValues = () => {
        // Generate years for dropdown. Should start 10 years back and to to this year + 1
        const currentYear = (new Date()).getFullYear();
        const years = Array.from({ length: 11 }, (_, i) => currentYear - i);
        const currentMonth = (new Date()).getMonth() + 1;
        this.setState({ years, currentYear, currentMonth });
    }

    componentDidMount() {
        this.loadAll();
        this.setDropdownValues();
        this.syncJournalSubscription = PubSub.subscribe(topics.SYNC_JOURNAL_UPDATED, (data) => {
            this.setState({ syncJournalNotification: data });
        });
    }

    componentWillUnmount() {
        if (this.syncJournalSubscription) {
            PubSub.unsubscribe(topics.SYNC_JOURNAL_UPDATED);
        }
    }

    render() {
        const {
            formatting = true,
            numberCopied = {},
            selected,
            searching = false,
            years = [],
            currentYear,
            currentMonth,
            closeBeforeCutoffDate,
            closeByDateCount,
            closeByDateLoading,
            showUnlockConfirm,
            unlockReason,
            showDeleteConfirm,
            deleteReason,
        } = this.state;
        const {
            productTitle,
            searchTitle,
            animaliaSearchTitle,
            reopenAreYouSure,
            reopenReasonPlaceholder,
            deleteAreYouSure,
            deleteReasonPlaceholder,
        } = this.props;
        const { userStore, journalStore } = this.props.stores;
        const { user, isAdmin, isAnimaliaActive, isCustomerAdmin } = userStore;
        const darkmode= util.getNestedValue(user, 'settings.darkmode');

        const { allJournals, visitors, animals, veterinaries, animalSpeciesList, totalJournals, cutoffDate, cutoffDateEnd, activeStatus, activeMode, veterinary, animalSpeciesId, searchText, currentPage, limit, total, templateFilter } = journalStore;

        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();

        const rows = allJournals.sort(fieldSorter(['-date']));
        const allSelected = this.isAllVisibleSelected(rows);

        const { syncJournalNotification } = this.state;

        return (
            <div class='w-100 d-flex justify-content-center' style='overflow-x: hidden; max-width: 100vw;'>
                <div class='w-100 h-100' style='padding-top: 0px; padding-bottom: 100px; overflow-x: hidden;'>

                    {syncJournalNotification && <div class='mx-3 mt-2'>
                        <SyncBanner
                            type='journal'
                            updatedByName={syncJournalNotification.updatedByName}
                            onRefresh={() => {
                                this.setState({ syncJournalNotification: null });
                                this.loadAll();
                            }}
                            onDismiss={() => this.setState({ syncJournalNotification: null })}
                        />
                    </div>}

                    <div class='w-100 d-flex flex-column'>
                        <div class='w-100 d-flex flex-column'>

                            <div class={`d-flex flex-column mx-2 mx-sm-3 px-2 px-sm-3 py-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>

                                <div class='d-flex flex-row flex-wrap justify-content-between' style='gap: 0.5rem;'>
                                    <div class='input-group input-group-sm flex-fill position-relative' style='min-width: 150px;'>
                                        <div class='input-group-text rounded-pill-left'>
                                            <i class='fa-regular fa-magnifying-glass'></i>
                                        </div>
                                        <input
                                            class={`form-control ${!searchText ? 'rounded-pill-right' : ''}`}
                                            type='text'
                                            value={searchText}
                                            placeholder={`${searchTitle}`}
                                            onInput={this.searchInput}
                                            onKeyDown={this.handleKeyDown}
                                        />
                                        {searchText && <>
                                            <button class='btn btn-danger rounded-pill-right' type='button' onClick={this.clearSearch}>
                                                <i class='fa-solid fa-circle-xmark'></i>
                                            </button>
                                        </>}

                                        {searching && <div class='position-absolute' style='right: 50px; top: 5px; z-index: 10000;'>
                                            <div class='spinner-border spinner-border-sm text-primary' role='status'>
                                                <span class='sr-only'>Loading...</span>
                                            </div>
                                        </div>}
                                    </div>
                                    <div class='input-group input-group-sm' style='width: auto; min-width: 70px; max-width: 100px;'>
                                        <select class='form-control rounded-pill text-center' onChange={this.onChooseLimit} value={limit}>
                                            <option value=''><Text id='dyrejournal.select-limit'>-- Limit --</Text></option>
                                            <option value='10'>10</option>
                                            <option value='25'>25</option>
                                            <option value='50'>50</option>
                                            <option value='75'>75</option>
                                            <option value='100'>100</option>
                                            <option value='500'>500</option>
                                            <option value='1000'>1000</option>
                                        </select>
                                    </div>
                                    <div class='input-group input-group-sm' style='width: auto; min-width: 80px; max-width: 120px;'>
                                        <select class='form-control rounded-pill text-center' onChange={this.onChooseStatus} value={activeStatus}>
                                            <option value=''><Text id='dyrejournal.select-status'>-- <Text id='dyrejournal.select-status-placeholder'>Status</Text> --</Text></option>
                                            <option value='open'><Text id='dyrejournal.status-open'>Open</Text></option>
                                            <option value='closed'><Text id='dyrejournal.status-closed'>Closed</Text></option>
                                        </select>
                                    </div>
                                    <div class='input-group input-group-sm' style='width: auto; min-width: 100px; max-width: 160px;'>
                                        <select class='form-control rounded-pill text-center' onChange={this.onChooseTemplateFilter} value={templateFilter || ''}>
                                            <option value=''>-- <Text id='dyrejournal.select-template-filter-placeholder'>Templates</Text> --</option>
                                            <option value='all'><Text id='dyrejournal.template-filter-all'>All templates</Text></option>
                                            <option value='template'><Text id='dyrejournal.template-filter-template'>Journal templates</Text></option>
                                            <option value='product'><Text id='dyrejournal.template-filter-product'>Product templates</Text></option>
                                        </select>
                                    </div>
                                    <div class='input-group input-group-sm flex-fill' style='min-width: 120px; max-width: 200px;'>
                                        {veterinaries && veterinaries.length > 0 && <>
                                            <select
                                                class='form-control rounded-pill'
                                                onInput={this.changeVeterinary}
                                                value={veterinary}
                                            >
                                                <option value=''>-- <Text id='dyrejournal.choose-vet'>Choose veterinary</Text> --</option>
                                                {veterinaries.map(vet => (
                                                    <option value={vet.id}>{`${vet.firstname} ${vet.lastname}`}</option>
                                                ))}
                                            </select>
                                        </>}
                                    </div>
                                    <div class='input-group input-group-sm flex-fill' style='min-width: 120px; max-width: 200px;'>
                                        {animalSpeciesList && animalSpeciesList.length > 0 && <>
                                            <select
                                                class='form-control rounded-pill'
                                                onInput={this.changeAnimalSpeciesId}
                                                value={animalSpeciesId || ''}
                                            >
                                                <option value=''>-- <Text id='dyrejournal.choose-species-filter'>Choose species</Text> --</option>
                                                {animalSpeciesList.map(sp => (
                                                    <option value={sp.id}>{sp.name}</option>
                                                ))}
                                            </select>
                                        </>}
                                    </div>
                                    {/* <div class='input-group ms-2 w-10'>
                                        <select class='form-control rounded-pill text-center ms-2' onChange={this.onChooseMode} value={activeMode}>
                                            <option value=''><Text id='dyrejournal.select-mode'>-- <Text id='dyrejournal.select-mode-placeholder'>Mode</Text> --</Text></option>
                                            <option value='clinic'><Text id='dyrejournal.mode-clinic'>Clinic</Text></option>
                                            <option value='production'><Text id='dyrejournal.mode-production'>Production</Text></option>
                                        </select>
                                    </div> */}

                                </div>

                                <div class='d-flex flex-row flex-wrap justify-content-end mt-1' style='gap: 0.5rem;'>

                                    <div class='input-group input-group-sm' style='width: auto; min-width: 140px; max-width: 200px;'>
                                        <div class='input-group-text rounded-pill-left'>
                                            <i class='fa-regular fa-timer d-none d-sm-inline me-sm-2'></i> <span class='d-none d-md-inline'>Fra</span>
                                        </div>
                                        <input
                                            class={`form-control rounded-${cutoffDate ? 'none' : 'pill-right'}`}
                                            type='date'
                                            onInput={this.changeCutoffDate}
                                            value={cutoffDate || ''}
                                        />
                                        {cutoffDate && <>
                                            <button class='btn btn btn-danger rounded-pill-right' type='button' onClick={this.clearCutoffDate}>
                                                <i class='fa-solid fa-circle-xmark'></i>
                                            </button>
                                        </>}
                                    </div>
                                    <div class='input-group input-group-sm' style='width: auto; min-width: 140px; max-width: 200px;'>
                                        <div class='input-group-text rounded-pill-left'>
                                            <i class='fa-regular fa-timer d-none d-sm-inline me-sm-2'></i> <span class='d-none d-md-inline'>Til</span>
                                        </div>
                                        <input
                                            class={`form-control rounded-${cutoffDateEnd ? 'none' : 'pill-right'}`}
                                            type='date'
                                            onInput={this.changeCutoffDateEnd}
                                            value={cutoffDateEnd || ''}
                                        />
                                        {cutoffDateEnd && <>
                                            <button class='btn btn-sm btn-danger rounded-pill-right' type='button' onClick={this.clearCutoffDateEnd}>
                                                <i class='fa-solid fa-circle-xmark'></i>
                                            </button>
                                        </>}
                                    </div>
                                    <div class='d-flex flex-row input-group input-group-sm' style='width: auto; min-width: 160px; max-width: 220px;'>
                                        <span class='input-group-text rounded-pill-left'>
                                            <i class='fa-regular fa-forward-fast'></i>
                                        </span>
                                        <select class='form-control form-control-sm rounded-none ms-0' style='min-width: 60px;' onChange={this.changeYear} value={currentYear}>
                                            <option value='0'><Text id='dyrejournal.select-year'>-- Choose year --</Text></option>
                                            {years.map((year, idx) => {
                                                return (<>
                                                    <option value={year}>{year}</option>
                                                </>);
                                            })}
                                        </select>
                                        <select class='form-control form-control-sm rounded-pill-right' style='min-width: 70px;' onChange={this.changeMonth} value={currentMonth}>
                                            <option value='0'><Text id='dyrejournal.select-month'>-- Choose month --</Text></option>
                                            {Array.from({ length: 12 }, (_, i) => i + 1).map((month, idx) => {
                                                const monthName = (new Date(2000, month - 1, 1)).toLocaleString('no-NO', { month: 'long' });
                                                return (<>
                                                    <option value={month}>{monthName}</option>
                                                </>);
                                            })}
                                        </select>
                                    </div>
                                    <div class=''>
                                        <button class='btn btn-sm btn-secondary rounded-pill' type='button' onClick={this.onClickGoToToday}>
                                            <nobr>
                                                <i class='fa-solid fa-calendar-day me-0 me-sm-2'></i>
                                                <span class='d-none d-sm-inline'><Text id='dyrejournal.go-to-today'>Go to today</Text></span>
                                            </nobr>
                                        </button>
                                    </div>
                                    {/* <button
                                        type='button'
                                        class='btn btn-sm btn-outline-primary rounded-pill ms-2'
                                        onClick={this.onClickGoToToday}
                                    >
                                        <nobr><Text id='dyrejournal.goto-today'>Goto today</Text></nobr>
                                    </button> */}
                                    {/* <button
                                        type='button'
                                        class='btn btn-sm btn-outline-primary rounded-pill ms-2'
                                        onClick={this.onClickPrintSales}
                                    >
                                        <nobr><i class='fa-duotone fa-print' /> <Text id='dyrejournal.print-sales'>Print sales</Text></nobr>
                                    </button> */}
                                    {/* <button
                                        type='button'
                                        class={`btn btn-sm btn${formatting ? '' : '-outline'}-primary rounded-pill ms-2`}
                                        onClick={this.onClickToggleFormatting}
                                    >
                                        <nobr><i class='fa-duotone fa-list' /> <Text id='dyrejournal.toggle-formatting'>Formatting</Text></nobr>
                                    </button> */}
                                    {/* <a
                                        href={`${apiServer}${journalStore.api?.load?.url}csv?jwtToken=${jwtToken}&status=${activeStatus || ''}&cutoffDate=${cutoffDate || ''}&cutoffDateEnd=${cutoffDateEnd || ''}&search=${search || ''}`}
                                        target='_blank'
                                        class={`btn btn-sm btn-outline-primary rounded-pill ms-2 pt-2`}
                                    >
                                        <nobr><i class='fa-duotone fa-download' /> <Text id='dyrejournal.download-csv'>Download CSV</Text></nobr>
                                    </a> */}
                                </div>
                                <div class='d-flex flex-row justify-content-end mt-1'>

                                </div>
                                {selected.size > 0 && <>
                                    <div class='d-flex flex-row justify-content-end mt-1'>
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-primary rounded-pill me-2'
                                            disabled={!selected.size}
                                            onClick={this.bulkCloseJournals}
                                        >
                                            <Text id='dyrejournal.close-selected-journals'>Close selected journals</Text> ({selected.size})
                                        </button>
                                        {/* <xmp>{JSON.stringify(Array.from(selected.keys()), null, 4)}</xmp> */}
                                    </div>
                                </>}

                                {isCustomerAdmin && <>
                                    <div class='d-flex flex-row flex-wrap justify-content-end align-items-center mt-2 pt-2 border-top' style='gap: 0.5rem;'>
                                        <span class='text-muted'>
                                            <i class='fa-solid fa-lock me-1' />
                                            <span class='d-none d-sm-inline'><Text id='dyrejournal.close-all-before'>Close all journals before:</Text></span>
                                        </span>
                                        <input
                                            type='date'
                                            class='form-control form-control-sm'
                                            style='width: 140px; min-width: 130px;'
                                            value={closeBeforeCutoffDate}
                                            onInput={this.onChangeCloseBeforeCutoffDate}
                                        />
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-outline-secondary rounded-pill'
                                            onClick={this.onClickCheckCloseByDate}
                                            disabled={!closeBeforeCutoffDate || closeByDateLoading}
                                        >
                                            {closeByDateLoading ? (
                                                <span class='spinner-border spinner-border-sm me-1' />
                                            ) : (
                                                <i class='fa-solid fa-magnifying-glass me-0 me-sm-1' />
                                            )}
                                            <span class='d-none d-sm-inline'><Text id='dyrejournal.check-count'>Check</Text></span>
                                        </button>
                                        {closeByDateCount !== null && (
                                            <span class={`badge ${closeByDateCount > 0 ? 'bg-warning text-dark' : 'bg-secondary'}`}>
                                                {closeByDateCount} <span class='d-none d-sm-inline'><Text id='dyrejournal.journals-found'>journals</Text></span>
                                            </span>
                                        )}
                                        <button
                                            type='button'
                                            class='btn btn-sm btn-danger rounded-pill'
                                            onClick={this.onClickCloseByDate}
                                            disabled={!closeBeforeCutoffDate || closeByDateCount === null || closeByDateCount === 0 || closeByDateLoading}
                                        >
                                            <i class='fa-solid fa-lock me-0 me-sm-1' />
                                            <span class='d-none d-sm-inline'><Text id='dyrejournal.close-journals'>Close journals</Text></span>
                                        </button>
                                    </div>
                                </>}
                                <div class='d-flex flex-row justify-content-end mt-1'>
                                    <div class='form-check form-check-inline'>
                                        <nobr>
                                            <input type='checkbox' class='form-check-input form-input-sm' id='showDeleted' onInput={this.toggleShowDeleted} />
                                            <label class='form-check-label' for='showDeleted'><Text id='dyrejournal.show-deleted'>Show deleted</Text></label>
                                        </nobr>
                                    </div>
                                </div>
                                <div class='d-flex flex-row justify-content-center mt-2'>
                                    <Pagination total={totalJournals} limit={limit} current={currentPage} onPageChange={this.setPage} stores={this.props.stores} minimalistic={true} />
                                </div>
                            </div>

                            <div class={`d-flex flex-column mx-2 mx-sm-3 mt-2 px-1 px-sm-3 py-2 box-container ${darkmode ? 'bg-darkmode' : 'bg-lightmode'}`}>

                                <div class='table-responsive mt-3'>
                                    <table class='table table-striped table-sm font-style-narrow'>
                                        <thead class=''>
                                            <tr>
                                                <th style='width:2.5rem;'>
                                                    <input
                                                        type='checkbox'
                                                        ref={el => (this.selectAllRef = el)}
                                                        checked={allSelected}
                                                        onChange={e => this.onToggleAllVisible(rows, e.currentTarget.checked)}
                                                        aria-label='Select all'
                                                    />
                                                </th>
                                                <th scope='col'>&nbsp;</th>
                                                <th scope='col'><Text id='animal.journal-id'>Id</Text></th>
                                                <th scope='col' class='text-center'><Text id='animal.journal-saleNumber'>saleNumber</Text></th>
                                                <th scope='col'><Text id='animal.journal-vet'>Vet</Text></th>
                                                <th scope='col'><Text id='animal.journal-date'>Journal date</Text></th>
                                                <th scope='col'><Text id='animal.journal-duration'>Duration</Text></th>
                                                <th scope='col'><Text id='animal.journal-animal'>Animal</Text></th>
                                                <th scope='col'><Text id='animal.journal-visitor'>Visitor</Text></th>
                                                <th scope='col'><Text id='animal.journal-chiefComplaint'>chiefComplaint</Text></th>
                                                <th scope='col'><Text id='animal.journal-treatmentPlan'>treatmentPlan</Text></th>
                                                <th scope='col'><Text id='animal.journal-comment'>comment</Text></th>
                                                <th scope='col'>&nbsp;</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {allJournals.sort(fieldSorter(['-date'])).map(j => {
                                                const diff = util.dateDiff(j.date);
                                                const lineSaleIsLocked = j.closed;
                                                const canReopen = canReopenJournal({ isAdmin, isCustomerAdmin, journal: j });
                                                const canDelete = canDeleteJournal({ isAdmin, isCustomerAdmin, journal: j });
                                                const canUndelete = canUndeleteJournal({ isAdmin, isCustomerAdmin, journal: j });
                                                const animal = animals.find(a => a.id === j.animal) || {};
                                                const visitor = visitors.find(v => v.id === j.visitor) || {};
                                                const veterinary = veterinaries.find(vet => vet.id === j.veterinary);
                                                const veterinaryName = veterinary ? `${veterinary.firstname} ${veterinary.lastname}` : j.veterinaryName;
                                                const vetInitials = getInitials(veterinaryName);
                                                // const hasMedicines = hasJournalWithMedicines(e);
                                                const hasPrescriptions = j.prescriptions && j.prescriptions.length > 0;

                                                const allDiagnoses = j.diagnoses && j.diagnoses.length > 0 ? j.diagnoses : [];
                                                const allMedicines = j.medicine && j.medicine.length > 0 ? j.medicine : [];

                                                const checked = this.state.selected.has(j.id);

                                                // Show the owning clinic name in the sale-number column only when the
                                                // user has access to more than one clinic (the journal list spans them).
                                                const saleClinicName = (userStore.currentCustomers || []).length > 1
                                                    ? ((userStore.currentCustomers || []).find(c => c.id === j.customer)?.name || '')
                                                    : '';

                                                return (<>
                                                    <tr
                                                        key={j.id} // ✅ stable key
                                                        class={`journalLine ${lineSaleIsLocked ? 'table-success' : ''} ${j.offline ? 'deleted text-muted' : ''}`}
                                                        onClick={lineSaleIsLocked ? this.onClickViewJournal : this.onClickEditJournal}
                                                        data-id={j.id}
                                                        data-sale={j.sale}
                                                        // style={`${lineSaleIsLocked ? 'background-color: rgba(144, 238, 144, 0.1);' : ''}`}
                                                    >
                                                        <td onClick={e => e.stopPropagation()}>
                                                            {!lineSaleIsLocked && <>
                                                                <input
                                                                    type='checkbox'
                                                                    checked={this.state.selected.has(j.id)}
                                                                    onClick={e => {
                                                                        e.stopPropagation();                            // stop row click
                                                                        this.onToggleOne(j.id, e.currentTarget.checked); // use checked from the click
                                                                    }}
                                                                    onPointerDown={e => e.stopPropagation()}             // extra safety on touch
                                                                    aria-label={`Select ${j.id}`}
                                                                />
                                                            </>}
                                                        </td>
                                                        <td>
                                                            {lineSaleIsLocked ? <i class='fas fa-square-check text-success'></i> : <i class='fas fa-pen-to-square text-danger'></i>}
                                                        </td>
                                                        <td>{j.journalNumber || j.id}</td>
                                                        <td class='text-center'>
                                                            {j.sale > 0
                                                                ? <a
                                                                    href={`/dyrejournal-sale/${j.sale}`}
                                                                    class='gotoSaleLink fw-bold text-decoration-none'
                                                                    data-sale={j.sale}
                                                                    data-customer={j.customer}
                                                                    onClick={this.onClickGotoSale}
                                                                >{j.saleNumber}</a>
                                                                : j.saleNumber
                                                            }
                                                            {' '}<i class='fa-light fa-copy' onClick={e => this.copyNumber(e, j.saleNumber)} />
                                                            {numberCopied[j.saleNumber] && <span class='text-success ms-1'><i class='fa-duotone fa-check' /></span>}
                                                            {saleClinicName && <div class='small text-body-secondary'>{saleClinicName}</div>}
                                                        </td>
                                                        <td>{vetInitials}</td>
                                                        <td>
                                                            <nobr>{util.formatDate(j.date, { hour12: false, hour: '2-digit', minute: '2-digit', locale: 'nb-NO' })}</nobr>
                                                        </td>
                                                        <td>
                                                            {j.endDate && j.date ? (() => {
                                                                const durationMs = new Date(j.endDate) - new Date(j.date);
                                                                const durationMin = Math.round(durationMs / (1000 * 60));
                                                                return `${durationMin} min`;
                                                            })() : ''}
                                                        </td>
                                                        <td>
                                                            {animal.name} ({animal.species}/{animal.breed})
                                                            {j.animalIsGroup && <span class='badge bg-info ms-2'>Group</span>}
                                                            {j.animalGroupSize > 1 && <span class='badge bg-secondary ms-1'>x{j.animalGroupSize}</span>}
                                                            {!!j.isTemplate && <span class='badge bg-primary ms-2'>
                                                                <i class='fa-solid fa-copy me-1' />
                                                                <Text id='dyrejournal.journal-template-badge'>Template</Text>{j.templateTitle ? `: ${j.templateTitle}` : ''}
                                                            </span>}
                                                            {Array.isArray(j.templateProducts) && j.templateProducts.map(p => (
                                                                <span class='badge bg-success ms-1'>
                                                                    <i class='fa-duotone fa-box me-1' />
                                                                    <Text id='dyrejournal.journal-product-template-badge'>Product</Text>: {p.name}
                                                                </span>
                                                            ))}
                                                        </td>
                                                        <td>
                                                            {/* {JSON.stringify(visitor)} */}
                                                            {localUtil.showName(visitor)}
                                                        </td>
                                                        <td>
                                                            {hasPrescriptions > 0 ? <>
                                                                <nobr>
                                                                    <small
                                                                        class={`me-2 bg-glow-danger text-white font-weight-light px-2 py-0 rounded-pill`}
                                                                        onClick={this.onClickShowPrescriptions}
                                                                        data-id={j.id}
                                                                        style='cursor: pointer;'
                                                                    >
                                                                        <i class='fa-solid fa-prescription-bottle me-1' />
                                                                        {hasPrescriptions}
                                                                    </small>
                                                                </nobr>
                                                            </> : <></>}
                                                            {allDiagnoses && allDiagnoses.length > 0 ? <>
                                                                <nobr>
                                                                    <small
                                                                        class={`ms-2 bg-glow-warning text-white font-weight-light px-2 py-0 rounded-pill`}
                                                                        onClick={this.onClickShowDiagnoses}
                                                                        data-id={j.id}
                                                                        style='cursor: pointer;'
                                                                    >
                                                                        <i class='fa-solid fa-stethoscope me-1' />
                                                                        {allDiagnoses.length}
                                                                    </small>
                                                                </nobr>
                                                            </> : <></>}

                                                            {allMedicines && allMedicines.length > 0 ? <>
                                                                <nobr>
                                                                    <small
                                                                        class={`ms-2 bg-glow-success text-white font-weight-light px-2 py-0 rounded-pill`}
                                                                        onClick={this.onClickShowMedicines}
                                                                        data-id={j.id}
                                                                        style='cursor: pointer;'
                                                                    >
                                                                        <i class='fa-solid fa-pills me-1' />
                                                                        {allMedicines.length}
                                                                    </small>
                                                                </nobr>
                                                            </> : <></>}

                                                            <span class='ms-2'>
                                                                <span class='d-inline d-lg-none'>{j.chiefComplaint ? j.chiefComplaint.substring(0, 50) : ''}{j.chiefComplaint && j.chiefComplaint.length > 50 ? '...' : ''}</span>
                                                                <span class='d-none d-lg-inline d-xxl-none'>{j.chiefComplaint ? j.chiefComplaint.substring(0, 120) : ''}{j.chiefComplaint && j.chiefComplaint.length > 120 ? '...' : ''}</span>
                                                                <span class='d-none d-xxl-inline'>{j.chiefComplaint}</span>
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span class='d-inline d-lg-none'>{j.treatmentPlan ? j.treatmentPlan.substring(0, 30) : ''}{j.treatmentPlan && j.treatmentPlan.length > 30 ? '...' : ''}</span>
                                                            <span class='d-none d-lg-inline d-xxl-none'>{j.treatmentPlan ? j.treatmentPlan.substring(0, 80) : ''}{j.treatmentPlan && j.treatmentPlan.length > 80 ? '...' : ''}</span>
                                                            <span class='d-none d-xxl-inline'>{j.treatmentPlan}</span>
                                                        </td>
                                                        <td>{j.title ? <span class='font-weight-bold me-1'>{j.title}</span> : ''}{j.comment}</td>
                                                        <td onClick={e => e.stopPropagation()}>
                                                            <nobr>
                                                                {canReopen ? <i class='fa-duotone fa-unlock me-2' style='cursor: pointer;' onClick={this.onClickUnlock} /> : ''}
                                                                {canDelete ? <i class='fa-duotone fa-trash text-danger' style='cursor: pointer;' onClick={this.onClickDeleteJournal} /> : ''}
                                                                {j.offline ? <>
                                                                    <small class='text-body-secondary'>
                                                                        <Text id='dyrejournal.journal.deleteReason'>Delete reason</Text>: {j.deleteReason || 'n/a'}
                                                                    </small>
                                                                    {canUndelete ? <i class='fa-duotone fa-trash-slash text-danger ms-2' style='cursor: pointer;' onClick={this.onClickUndeleteJournal} /> : ''}
                                                                </> : ''}
                                                            </nobr>
                                                        </td>
                                                    </tr>
                                                </>)
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                            </div>
                        </div>

                    </div>

                    <Pagination total={totalJournals} limit={limit} current={currentPage} onPageChange={this.setPage} stores={this.props.stores} />

                    {showUnlockConfirm && (
                        <div
                            class='modal d-block'
                            tabindex='-1'
                            style='background-color: rgba(0,0,0,0.6); z-index: 1060;'
                            onClick={this.cancelUnlockJournal}
                        >
                            <div class='modal-dialog modal-dialog-centered' onClick={e => e.stopPropagation()}>
                                <div class='modal-content border border-4 border-warning shadow-lg' style='border-radius: 0;'>
                                    <div class='modal-header bg-warning text-dark' style='border-radius: 0;'>
                                        <h4 class='modal-title mb-0'>
                                            <i class='fa-duotone fa-unlock me-2' />
                                            <Text id='journal.reopenConfirmTitle'>Reopen journal?</Text>
                                        </h4>
                                        <button type='button' class='btn-close' aria-label='Close' onClick={this.cancelUnlockJournal} />
                                    </div>
                                    <div class='modal-body py-4'>
                                        <p class='fs-5'>{reopenAreYouSure}</p>
                                        <label class='form-label mb-1' for='journalReopenReason'>
                                            <Text id='journal.reopenReason'>Reason for reopening (required)</Text>
                                        </label>
                                        <textarea
                                            id='journalReopenReason'
                                            class='form-control'
                                            rows='3'
                                            value={unlockReason}
                                            placeholder={reopenReasonPlaceholder}
                                            onInput={e => this.setState({ unlockReason: e.target.value })}
                                        />
                                        <small class='text-body-secondary'>
                                            <Text id='journal.reopenReason-help'>The reason is stored in the journal history.</Text>
                                        </small>
                                    </div>
                                    <div class='modal-footer'>
                                        <button type='button' class='btn btn-lg btn-outline-secondary rounded-pill px-4' onClick={this.cancelUnlockJournal}>
                                            <Text id='journal.unlockConfirmCancel'>Cancel</Text>
                                        </button>
                                        <button
                                            type='button'
                                            class='btn btn-lg btn-warning rounded-pill px-4'
                                            disabled={!isValidReopenReason(unlockReason)}
                                            onClick={this.confirmUnlockJournal}
                                        >
                                            <i class='fa-duotone fa-unlock me-2' />
                                            <Text id='journal.reopenConfirmConfirm'>Reopen journal</Text>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {showDeleteConfirm && (
                        <div
                            class='modal d-block'
                            tabindex='-1'
                            style='background-color: rgba(0,0,0,0.6); z-index: 1060;'
                            onClick={this.cancelDeleteJournal}
                        >
                            <div class='modal-dialog modal-dialog-centered' onClick={e => e.stopPropagation()}>
                                <div class='modal-content border border-4 border-danger shadow-lg' style='border-radius: 0;'>
                                    <div class='modal-header bg-danger text-white' style='border-radius: 0;'>
                                        <h4 class='modal-title mb-0'>
                                            <i class='fa-duotone fa-trash me-2' />
                                            <Text id='journal.deleteConfirmTitle'>Delete journal?</Text>
                                        </h4>
                                        <button type='button' class='btn-close btn-close-white' aria-label='Close' onClick={this.cancelDeleteJournal} />
                                    </div>
                                    <div class='modal-body py-4'>
                                        <p class='fs-5'>{deleteAreYouSure}</p>
                                        <label class='form-label mb-1' for='journalDeleteReason'>
                                            <Text id='journal.deleteReason'>Reason for deleting (required)</Text>
                                        </label>
                                        <textarea
                                            id='journalDeleteReason'
                                            class='form-control'
                                            rows='3'
                                            value={deleteReason}
                                            placeholder={deleteReasonPlaceholder}
                                            onInput={e => this.setState({ deleteReason: e.target.value })}
                                        />
                                        <small class='text-body-secondary'>
                                            <Text id='journal.deleteReason-help'>The reason is stored in the journal history.</Text>
                                        </small>
                                    </div>
                                    <div class='modal-footer'>
                                        <button type='button' class='btn btn-lg btn-outline-secondary rounded-pill px-4' onClick={this.cancelDeleteJournal}>
                                            <Text id='journal.unlockConfirmCancel'>Cancel</Text>
                                        </button>
                                        <button
                                            type='button'
                                            class='btn btn-lg btn-danger rounded-pill px-4'
                                            disabled={!isValidDeleteReason(deleteReason)}
                                            onClick={this.confirmDeleteJournal}
                                        >
                                            <i class='fa-duotone fa-trash me-2' />
                                            <Text id='journal.deleteConfirmConfirm'>Delete journal</Text>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        );
    }
}

export default AllJournals;
